import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, Subject, firstValueFrom, take, takeUntil,finalize } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ReservationApiService, SearchOrdering, TimeToSearch } from '../apis/reservation-api.service';
import { UtilsService } from '../lib/utils.service';
import { ChainAccount } from '../model/employee';
import { Reservation } from '../model/reservation';
import * as ReservationListActions from '../store/actions/reservation-list.actions';
import * as AppSelectors from '../store/selectors/app.selectors';
import * as EmployeeSelectors from '../store/selectors/employee.selectors';
import * as ReservationListSelectors from '../store/selectors/reservation-list.selectors';
import * as AppActions from '../store/actions/app.actions';

@Component({
  selector: 'app-reservation-search',
  templateUrl: './reservation-search.component.html',
  styleUrls: ['./reservation-search.component.scss']
})
export class ReservationSearchComponent implements OnInit, OnDestroy {

  constructor(
    private reservationApiService: ReservationApiService,
    private translate: TranslateService,
    private utilsService: UtilsService,
    private store: Store<{}>,
    private spinner: NgxSpinnerService,
  ) { }

  // @HostBinding('class.compact-view') get compactViewClass() { return this.compactView; }

  destroy$: Subject<boolean> = new Subject<boolean>();

  restaurantIds$: Observable<string[] | undefined> = this.store.pipe(select(EmployeeSelectors.selectRestaurantIds));
  restaurants$: Observable<ChainAccount[] | undefined> = this.store.pipe(select(EmployeeSelectors.selectRestaurants));
  restaurantId$: Observable<number | undefined> = this.store.pipe(select(AppSelectors.selectRestaurantId));
  globalSearchTerm$: Observable<string> = this.store.pipe(select(ReservationListSelectors.selectGlobalSearchTerm));
  restaurantName$: Observable<string> = this.store.pipe(select(AppSelectors.selectRestaurantName));
  isOffline$: Observable<boolean> = this.store.pipe(select(AppSelectors.selectIsOffline));
  isOffline: boolean = false;
  compactView$: Observable<boolean | null> = this.store.pipe(select(ReservationListSelectors.selectCompactView));
  compactView: boolean | null = null;

  searchTerm: string = '';
  timeToSearch: string = 'TODAY';

  timesToSearch: { display: string, value: string }[] = [
    { display: this.translate.instant('reservations.search.upcoming'), value: 'TODAY' },
    { display: this.translate.instant('reservations.search.past'), value: 'PAST' }
  ];

  reservations: Reservation[] = [];

  loadingSpinnerTimeoutId: null | ReturnType<typeof setTimeout> = null;

  async ngOnInit(): Promise<void> {
    this.searchTerm = await firstValueFrom(this.globalSearchTerm$);
    this.searchReservations();

    this.isOffline$.pipe(takeUntil(this.destroy$)).subscribe((isOffline) => {
      this.isOffline = isOffline;
    });

    this.compactView$.pipe(takeUntil(this.destroy$)).subscribe((compactView) => {
      this.compactView = compactView;
    });
  }

  async searchReservations() {
    let globalSearchTerm = this.searchTerm?.trim() ?? '';
    if (globalSearchTerm == null || globalSearchTerm.length <= 0) {
      return;
    }

    const termIsTelephone = !isNaN(parseInt(globalSearchTerm));
    if (termIsTelephone) {
      globalSearchTerm = globalSearchTerm.replace(/ /g, '');
    }

    const restaurantId = await firstValueFrom(this.restaurantId$);

    if (restaurantId == null) {
      console.error('Restaurant Id does not exist!');
      return;
    }

    let restaurantIds = (await firstValueFrom(this.restaurantIds$)) ?? [];
    const date = moment(new Date()).format('yyyy-MM-DD');
    const timeToSearch = this.timeToSearch as TimeToSearch;
    const searchOrdering: SearchOrdering = timeToSearch === 'PAST' ? 'DESC' : 'ASC';

    if (restaurantIds == null || restaurantIds.length == 0 || !this.utilsService.canWrite("SearchReservationChain")) {
      restaurantIds = [restaurantId + ""];
    }

    this.showLoader();

    this.reservationApiService.reservationSearch(restaurantId, restaurantIds, globalSearchTerm, date, timeToSearch, searchOrdering)
      .pipe(
        take(1),
        finalize(() => this.hideLoader())
      )
      .subscribe({
        next: (data) => {
          this.reservations = data.reservationsList ?? [];
        },
        error: (err) => {
          console.error('Reservation search failed', err);

          this.store.dispatch({
            type: AppActions.showAlert.type,
            alert: this.translate.instant('messages.somethingWrongHappened')
          });
        }
      });
  }

  showLoader() {
    this.hideLoader();

    this.loadingSpinnerTimeoutId = setTimeout(() => {
      this.spinner.show();
    }, environment.requestOverlayThreashold);
  }

  hideLoader() {
    if (this.loadingSpinnerTimeoutId != null) {
      clearTimeout(this.loadingSpinnerTimeoutId);
    }

    this.spinner.hide();
  }

  ngOnDestroy() {
    this.store.dispatch({ type: ReservationListActions.setGlobalSearchTerm.type, term: '' });
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
